import { Form, Formik } from 'formik'
import * as Yup from 'yup'

import { NotificationType, notify } from 'src/providers/NotificationProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Heading3 } from 'src/components/atoms/Typography'
import { FormField } from 'src/components/molecules/FormField'
import { HStack } from 'src/components/templates/HStack'

import {
  namedOperations,
  useUpdateGiftTemplateMutation
} from 'src/config/generated/graphql'
import { FormFieldTypes } from 'src/types/form'

const SUBJECT_PREFIX = 'A gift for you from '

export interface EditGiftTemplateModalProps {
  id?: string
  title?: string
  subject?: string
  closeModal: () => void
}

interface EditGiftTemplateFormValues {
  Title: string
  SenderName: string
}

const validationSchema = Yup.object({
  Title: Yup.string().required('Please enter a title'),
  SenderName: Yup.string().required('Please enter the sender name')
})

export const EditGiftTemplateModal = ({
  id,
  title,
  subject,
  closeModal
}: EditGiftTemplateModalProps) => {
  const [updateGiftTemplate] = useUpdateGiftTemplateMutation({
    refetchQueries: [namedOperations.Query.listAdminGiftTemplates]
  })

  const initialValues: EditGiftTemplateFormValues = {
    Title: title || '',
    SenderName: subject?.startsWith(SUBJECT_PREFIX)
      ? subject.slice(SUBJECT_PREFIX.length)
      : subject || ''
  }

  const handleSubmit = async (values: EditGiftTemplateFormValues) => {
    if (!id) return

    try {
      await updateGiftTemplate({
        variables: {
          input: {
            Id: id,
            Title: values.Title,
            SenderName: values.SenderName
          }
        }
      })
      notify({
        message: 'Gift template updated',
        type: NotificationType.success
      })
      closeModal()
    } catch {
      notify({
        message: 'Could not update the gift template',
        type: NotificationType.error
      })
    }
  }

  return (
    <div className='w-full sm:w-[520px]'>
      <Heading3>Edit certificate</Heading3>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <FormField id='Title' label='Title' type={FormFieldTypes.text} />
          <FormField
            id='SenderName'
            label='Sender name'
            description='Used in the email subject: “A gift for you from {sender}”.'
            type={FormFieldTypes.text}
          />
          <HStack className='mt-8 flex justify-between'>
            <ButtonRebrand variant='outline' onClick={closeModal}>
              Cancel
            </ButtonRebrand>
            <ButtonRebrand variant='lime' type='submit'>
              Save changes
            </ButtonRebrand>
          </HStack>
        </Form>
      </Formik>
    </div>
  )
}
